import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { emailIntelligence, competitors as competitorsApi } from "@/lib/api";
import type { EmailConnection, EmailInsight, EmailStats, EmailCategory, Competitor } from "@/types";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<EmailCategory, string> = {
  competitor_newsletter: "Newsletter",
  seo_report:           "SEO Report",
  testimonial:          "Testimonial",
  case_study:           "Case Study",
  product_update:       "Product Update",
  pricing_change:       "Pricing Change",
  other:                "Other",
};

const CATEGORY_COLORS: Record<EmailCategory, string> = {
  competitor_newsletter: "text-blue-400 border-blue-400/20 bg-blue-400/10",
  seo_report:           "text-emerald-400 border-emerald-400/20 bg-emerald-400/10",
  testimonial:          "text-purple-400 border-purple-400/20 bg-purple-400/10",
  case_study:           "text-amber-400 border-amber-400/20 bg-amber-400/10",
  product_update:       "text-sky-400 border-sky-400/20 bg-sky-400/10",
  pricing_change:       "text-red-400 border-red-400/20 bg-red-400/10",
  other:                "text-zinc-400 border-zinc-400/20 bg-zinc-400/10",
};

const SIGNAL_COLORS: Record<string, string> = {
  high:   "text-red-400 border-red-400/20 bg-red-400/10",
  medium: "text-amber-400 border-amber-400/20 bg-amber-400/10",
  low:    "text-zinc-400 border-zinc-400/20 bg-zinc-400/10",
};

const REDIRECT_URI = `${window.location.origin}/email-intelligence/callback`;

// ─── Stats row ────────────────────────────────────────────────────────────────

function StatsRow({ stats, isLoading }: { stats: EmailStats | null; isLoading: boolean }) {
  const items = [
    { label: "Total emails", value: stats?.total ?? 0, icon: "mail-01" },
    { label: "Unread",        value: stats?.unread ?? 0, icon: "mail-open-01" },
    { label: "SEO reports",   value: stats?.seoReports ?? 0, icon: "chart-bar-increasing" },
    { label: "Testimonials",  value: stats?.withTestimonials ?? 0, icon: "star-01" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 border border-border rounded-lg overflow-hidden">
      {items.map((item, i) => (
        <div key={item.label} className={cn(
          "flex flex-col gap-1 p-4 bg-card",
          i % 2 === 0 && i < 3 && "border-r border-border",
          i === 1 && "border-r sm:border-r border-border",
          i < 2 && "border-b sm:border-b-0 border-border",
          i === 2 && "border-r border-border"
        )}>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{item.label}</p>
            <div className="rounded border border-border p-1">
              <Icon name={item.icon} size={12} className="text-muted-foreground" />
            </div>
          </div>
          {isLoading ? (
            <Skeleton className="h-8 w-12 mt-1" />
          ) : (
            <p className="font-mono text-3xl font-bold tracking-tight leading-none mt-1">{item.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Connect banner ───────────────────────────────────────────────────────────

function ConnectBanner({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <div className="h-12 w-12 shrink-0 rounded-lg border border-border bg-muted flex items-center justify-center">
        <Icon name="mail-01" size={22} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm font-semibold">Connect Gmail</p>
        <p className="font-sans text-xs text-muted-foreground mt-0.5 leading-relaxed">
          Connect your Gmail inbox and Mira will scan competitor newsletters, SEO reports, testimonials, and pricing signals — turning your inbox into competitive intelligence.
        </p>
      </div>
      <Button onClick={onConnect} className="shrink-0 w-full sm:w-auto">
        <Icon name="mail-01" size={13} className="mr-2" />
        Connect Gmail
      </Button>
    </div>
  );
}

// ─── Connection card ──────────────────────────────────────────────────────────

function ConnectionCard({
  conn,
  onSync,
  onDisconnect,
  isSyncing,
}: {
  conn: EmailConnection;
  onSync: () => void;
  onDisconnect: () => void;
  isSyncing: boolean;
}) {
  const isBackgroundSyncing = !conn.syncComplete || isSyncing;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <div className="h-8 w-8 shrink-0 rounded-full border border-border bg-muted flex items-center justify-center overflow-hidden">
        <img
          src={`https://www.google.com/s2/favicons?domain=gmail.com&sz=32`}
          alt="Gmail"
          className="h-5 w-5 object-contain"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans text-xs font-medium truncate">{conn.email}</p>
        <p className="font-mono text-[10px] text-muted-foreground">
          {isBackgroundSyncing
            ? "Syncing in background..."
            : conn.lastSyncedAt
            ? `Last synced ${new Date(conn.lastSyncedAt).toLocaleString()}`
            : "Never synced"}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button size="sm" variant="outline" className="text-[11px]" onClick={onSync} disabled={isBackgroundSyncing}>
          {isBackgroundSyncing
            ? <><Icon name="refresh" size={11} className="mr-1.5 animate-spin" />Syncing…</>
            : <><Icon name="refresh" size={11} className="mr-1.5" />Sync</>
          }
        </Button>
        <Button size="sm" variant="ghost" className="px-2 text-[10px] text-muted-foreground hover:text-destructive" onClick={onDisconnect}>
          Disconnect
        </Button>
      </div>
    </div>
  );
}

// ─── Insight card ─────────────────────────────────────────────────────────────

function InsightCard({ insight, onClick }: { insight: EmailInsight; onClick: () => void }) {
  const catStyle = CATEGORY_COLORS[insight.category] ?? CATEGORY_COLORS.other;
  const sigStyle = SIGNAL_COLORS[insight.signalScore] ?? SIGNAL_COLORS.low;
  const hasSeo  = insight.seoData && (
    (insight.seoData.keywords?.length ?? 0) > 0 ||
    (insight.seoData.ranking_claims?.length ?? 0) > 0 ||
    (insight.seoData.traffic_claims?.length ?? 0) > 0
  );
  const hasTestimonials = insight.testimonials?.length > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left border rounded-lg overflow-hidden transition-all hover:border-foreground/30",
        insight.isRead ? "border-border bg-card opacity-70" : "border-border bg-card"
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-4 py-3 border-b border-border/50">
        <div className="h-7 w-7 shrink-0 rounded border border-border bg-muted flex items-center justify-center overflow-hidden mt-0.5">
          <img
            src={`https://www.google.com/s2/favicons?domain=${insight.senderDomain}&sz=32`}
            alt=""
            className="h-4 w-4 object-contain"
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-sans text-xs font-semibold truncate max-w-[200px]">{insight.subject}</span>
            {!insight.isRead && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />}
          </div>
          <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{insight.senderDomain}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="outline" className={cn("text-[10px] h-4 px-1.5", catStyle)}>
            {CATEGORY_LABELS[insight.category]}
          </Badge>
          <Badge variant="outline" className={cn("text-[10px] h-4 px-1.5", sigStyle)}>
            {insight.signalScore}
          </Badge>
        </div>
      </div>

      {/* Summary */}
      {insight.summary && (
        <p className="px-4 py-2 font-sans text-xs text-muted-foreground leading-relaxed line-clamp-2 border-b border-border/50">
          {insight.summary}
        </p>
      )}

      {/* Data chips */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        {insight.competitor && (
          <div className="flex items-center gap-1 text-muted-foreground/60">
            <Icon name="radar-01" size={11} />
            <span className="font-mono text-[10px]">{insight.competitor.name}</span>
          </div>
        )}
        {hasSeo && (
          <div className="flex items-center gap-1 text-emerald-400/70">
            <Icon name="chart-bar-increasing" size={11} />
            <span className="font-mono text-[10px]">SEO data</span>
          </div>
        )}
        {hasTestimonials && (
          <div className="flex items-center gap-1 text-purple-400/70">
            <Icon name="star-01" size={11} />
            <span className="font-mono text-[10px]">{insight.testimonials.length} testimonial{insight.testimonials.length !== 1 ? "s" : ""}</span>
          </div>
        )}
        <span className="font-mono text-[10px] text-muted-foreground/40 ml-auto">
          {new Date(insight.receivedAt).toLocaleDateString()}
        </span>
      </div>
    </button>
  );
}

// ─── Insight detail panel ─────────────────────────────────────────────────────

function InsightDetail({ insight, onClose }: { insight: EmailInsight; onClose: () => void }) {
  const catStyle = CATEGORY_COLORS[insight.category] ?? CATEGORY_COLORS.other;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start gap-3 px-5 py-4 border-b border-border shrink-0">
        <div className="flex-1 min-w-0">
          <p className="font-sans text-sm font-semibold leading-snug">{insight.subject}</p>
          <p className="font-mono text-[11px] text-muted-foreground mt-1">
            From {insight.senderName ? `${insight.senderName} · ` : ""}{insight.senderEmail}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {new Date(insight.receivedAt).toLocaleString()}
          </p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <Badge variant="outline" className={cn("text-[10px]", catStyle)}>
              {CATEGORY_LABELS[insight.category]}
            </Badge>
            <Badge variant="outline" className={cn("text-[10px]", SIGNAL_COLORS[insight.signalScore])}>
              {insight.signalScore} signal
            </Badge>
            {insight.competitor && (
              <Badge variant="outline" className="text-[10px]">
                {insight.competitor.name}
              </Badge>
            )}
          </div>
        </div>
        <button onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-accent text-muted-foreground shrink-0">
          <Icon name="cancel-01" size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Summary */}
        {insight.summary && (
          <div className="space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Summary</p>
            <p className="font-sans text-xs leading-relaxed">{insight.summary}</p>
          </div>
        )}

        <Separator />

        {/* SEO data */}
        {insight.seoData && Object.values(insight.seoData).some(v => (v as unknown[])?.length > 0) && (
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Icon name="chart-bar-increasing" size={12} className="text-emerald-400" />SEO Data
            </p>
            {(insight.seoData.keywords?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="font-mono text-[10px] text-muted-foreground/60">Keywords mentioned</p>
                <div className="flex flex-wrap gap-1.5">
                  {insight.seoData.keywords!.map((kw, i) => (
                    <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded border border-border bg-muted">{kw}</span>
                  ))}
                </div>
              </div>
            )}
            {(insight.seoData.ranking_claims?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="font-mono text-[10px] text-muted-foreground/60">Ranking claims</p>
                {insight.seoData.ranking_claims!.map((c, i) => (
                  <p key={i} className="font-sans text-xs text-muted-foreground flex items-start gap-1.5">
                    <Icon name="checkmark-circle-01" size={12} className="text-emerald-400 mt-0.5 shrink-0" />{c}
                  </p>
                ))}
              </div>
            )}
            {(insight.seoData.traffic_claims?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="font-mono text-[10px] text-muted-foreground/60">Traffic claims</p>
                {insight.seoData.traffic_claims!.map((c, i) => (
                  <p key={i} className="font-sans text-xs text-muted-foreground flex items-start gap-1.5">
                    <Icon name="chart-bar-increasing" size={12} className="text-sky-400 mt-0.5 shrink-0" />{c}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Testimonials */}
        {insight.testimonials?.length > 0 && (
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Icon name="star-01" size={12} className="text-purple-400" />Testimonials
            </p>
            {insight.testimonials.map((t, i) => (
              <div key={i} className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-2">
                <p className="font-sans text-xs italic leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {t.author}{t.company ? ` · ${t.company}` : ""}
                  </p>
                  <Badge variant="outline" className={cn("text-[10px]",
                    t.sentiment === "positive" ? "text-emerald-400 border-emerald-400/20" :
                    t.sentiment === "negative" ? "text-red-400 border-red-400/20" :
                    "text-zinc-400 border-zinc-400/20"
                  )}>
                    {t.sentiment}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Key claims */}
        {insight.keyClaims?.length > 0 && (
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Icon name="flash" size={12} className="text-amber-400" />Key Claims
            </p>
            {insight.keyClaims.map((cl, i) => (
              <div key={i} className="space-y-0.5">
                <p className="font-sans text-xs font-medium">{cl.claim}</p>
                {cl.evidence && <p className="font-sans text-xs text-muted-foreground">{cl.evidence}</p>}
                <Badge variant="outline" className="text-[10px] mt-1">{cl.category}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function EmailIntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [insights, setInsights] = useState<EmailInsight[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<EmailInsight | null>(null);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<EmailConnection | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Active filters
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterSignal, setFilterSignal] = useState<string>("");
  const [filterQuery, setFilterQuery] = useState("");
  const [filterRead, setFilterRead] = useState<string>("");
  const [filterCompetitor, setFilterCompetitor] = useState("");
  const [filterDomain, setFilterDomain] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const insightsRequestRef = useRef(0);

  const limit = 20;

  useEffect(() => {
    competitorsApi.list().then(res => {
      if ("data" in res && res.data) setCompetitors(res.data);
    });
  }, []);

  // ── Handle OAuth callback ──
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code) return;

    // Remove code from URL immediately
    setSearchParams({}, { replace: true });

    setIsConnecting(true);
    emailIntelligence.connect(code, REDIRECT_URI).then(res => {
      if ("data" in res && res.data) {
        toast.success(`Connected ${res.data.email}`);
        setConnections(prev => {
          const without = prev.filter(c => c.id !== res.data!.id);
          return [res.data!, ...without];
        });
        toast.success("Gmail connected. Background sync started.");
      } else if ("error" in res) {
        toast.error(res.error.message);
      }
      setIsConnecting(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConnections = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoadingConnections(true);
    const res = await emailIntelligence.connections();
    if ("data" in res && res.data) setConnections(res.data);
    if (showLoading) setIsLoadingConnections(false);
  }, []);

  const loadInsights = useCallback(async (p = 1, showLoading = true) => {
    const requestId = ++insightsRequestRef.current;
    if (showLoading) setIsLoadingInsights(true);
    const res = await emailIntelligence.insights({
      category: filterCategory || undefined,
      signalScore: filterSignal || undefined,
      q: filterQuery.trim() || undefined,
      isRead: filterRead || undefined,
      competitorId: filterCompetitor || undefined,
      domain: filterDomain || undefined,
      from: filterFrom ? `${filterFrom}T00:00:00.000Z` : undefined,
      to: filterTo ? `${filterTo}T23:59:59.999Z` : undefined,
      page: p,
      limit,
    });
    if (requestId !== insightsRequestRef.current) return;
    if ("data" in res && res.data) {
      setInsights(res.data.insights);
      setTotal(res.data.total);
      setPage(p);
    }
    if (showLoading) setIsLoadingInsights(false);
  }, [filterCategory, filterSignal, filterQuery, filterRead, filterCompetitor, filterDomain, filterFrom, filterTo]);

  const loadStats = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoadingStats(true);
    const res = await emailIntelligence.stats();
    if ("data" in res && res.data) setStats(res.data);
    if (showLoading) setIsLoadingStats(false);
  }, []);

  useEffect(() => { loadConnections(); loadStats(); }, [loadConnections, loadStats]);
  useEffect(() => {
    const timer = window.setTimeout(() => { void loadInsights(1); }, filterQuery ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [loadInsights, filterQuery]);

  const hasActiveSync = connections.some(connection => !connection.syncComplete);
  useEffect(() => {
    if (!hasActiveSync) return;
    const timer = window.setInterval(() => {
      void loadConnections(false);
      void loadInsights(1, false);
      void loadStats(false);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [hasActiveSync, loadConnections, loadInsights, loadStats]);

  const handleConnect = () => setConsentOpen(true);

  const beginGmailConnect = async () => {
    setConsentOpen(false);
    setIsConnecting(true);
    const res = await emailIntelligence.gmailAuthUrl(REDIRECT_URI);
    if ("data" in res && res.data) {
      window.location.href = res.data.url;
    } else {
      toast.error("Failed to get auth URL");
      setIsConnecting(false);
    }
  };

  const syncConnection = useCallback(async (connId: string) => {
    setSyncingId(connId);
    try {
      const res = await emailIntelligence.startSync(connId);
      if ("data" in res && res.data?.queued) {
        setConnections(prev => prev.map(connection => connection.id === connId
          ? { ...connection, syncComplete: false }
          : connection));
        toast.success("Background email sync started");
      }
      else if ("error" in res) toast.error(res.error?.message ?? "Sync error");
    } finally {
      setSyncingId(null);
    }
  }, []);

  const handleSync = (connId: string) => { void syncConnection(connId); };

  const handleDisconnect = (connId: string) => {
    const connection = connections.find(item => item.id === connId);
    if (connection) setDisconnectTarget(connection);
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    const connId = disconnectTarget.id;
    setDisconnectTarget(null);
    const res = await emailIntelligence.disconnect(connId);
    if ("data" in res) {
      toast.success("Account disconnected");
      setConnections(prev => prev.filter(c => c.id !== connId));
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
  };

  const handleInsightClick = async (insight: EmailInsight) => {
    setSelectedInsight(insight);
    if (!insight.isRead) {
      // Mark read optimistically
      setInsights(prev => prev.map(i => i.id === insight.id ? { ...i, isRead: true } : i));
      setStats(prev => prev ? { ...prev, unread: Math.max(0, prev.unread - 1) } : prev);
      await emailIntelligence.insight(insight.id); // server marks read
    }
  };

  const CATEGORIES: { value: string; label: string }[] = [
    { value: "", label: "All" },
    { value: "competitor_newsletter", label: "Newsletter" },
    { value: "seo_report", label: "SEO Report" },
    { value: "testimonial", label: "Testimonial" },
    { value: "case_study", label: "Case Study" },
    { value: "product_update", label: "Product Update" },
    { value: "pricing_change", label: "Pricing Change" },
  ];

  const hasConnections = connections.length > 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Email Intel"
        actions={
          <div className="flex items-center gap-2">
            {isConnecting && (
              <span className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Icon name="refresh" size={11} className="animate-spin" />Connecting…
              </span>
            )}
            {hasConnections && (
              <Button size="sm" onClick={handleConnect} variant="outline">
                <Icon name="add-01" size={12} className="mr-1.5" />Add account
              </Button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {/* Top section: stats + connections */}
        <div className="px-4 sm:px-6 pt-4 pb-3 space-y-3 border-b border-border shrink-0">
          <StatsRow stats={stats} isLoading={isLoadingStats} />

          {/* Connections */}
          {isLoadingConnections ? (
            <Skeleton className="h-14 w-full rounded-lg" />
          ) : !hasConnections ? (
            <ConnectBanner onConnect={handleConnect} />
          ) : (
            <div className="space-y-2">
              {connections.map(conn => (
                <ConnectionCard
                  key={conn.id}
                  conn={conn}
                  onSync={() => handleSync(conn.id)}
                  onDisconnect={() => handleDisconnect(conn.id)}
                  isSyncing={syncingId === conn.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Filter bar */}
        {hasConnections && (
          <div className="flex flex-wrap items-center gap-1.5 px-4 sm:px-6 py-2.5 border-b border-border shrink-0">
            <input
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              placeholder="Search sender or subject"
              className="h-7 w-48 rounded-md border border-border bg-background px-2.5 font-sans text-xs outline-none placeholder:text-muted-foreground/50 focus:border-foreground/40"
            />
            <input
              value={filterDomain}
              onChange={e => setFilterDomain(e.target.value.toLowerCase())}
              onBlur={() => setFilterDomain(value => value.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].replace(/^.*@/, "").trim())}
              placeholder="Sender domain"
              className="h-7 w-36 rounded-md border border-border bg-background px-2.5 font-mono text-[10px] outline-none placeholder:text-muted-foreground/50 focus:border-foreground/40"
            />
            <Select value={filterCategory || "all"} onValueChange={value => setFilterCategory(value === "all" ? "" : value)}>
              <SelectTrigger triggerSize="sm" className="w-auto min-w-32 gap-2 px-2.5 pr-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => <SelectItem key={cat.value || "all"} value={cat.value || "all"}>{cat.label} category</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterSignal || "all"} onValueChange={value => setFilterSignal(value === "all" ? "" : value)}>
              <SelectTrigger triggerSize="sm" className="w-auto min-w-28 gap-2 px-2.5 pr-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <SelectValue placeholder="Signal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All signals</SelectItem>
                <SelectItem value="high">High signal</SelectItem>
                <SelectItem value="medium">Medium signal</SelectItem>
                <SelectItem value="low">Low signal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRead || "all"} onValueChange={value => setFilterRead(value === "all" ? "" : value)}>
              <SelectTrigger triggerSize="sm" className="w-auto min-w-24 gap-2 px-2.5 pr-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="false">Unread</SelectItem>
                <SelectItem value="true">Read</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCompetitor || "all"} onValueChange={value => setFilterCompetitor(value === "all" ? "" : value)}>
              <SelectTrigger triggerSize="sm" className="w-auto max-w-44 gap-2 px-2.5 pr-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <SelectValue placeholder="Competitor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All competitors</SelectItem>
                {competitors.map(competitor => <SelectItem key={competitor.id} value={competitor.id}>{competitor.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} aria-label="From date" className="h-7 rounded-md border border-border bg-background px-2 font-mono text-[10px] text-muted-foreground outline-none" />
            <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} aria-label="To date" className="h-7 rounded-md border border-border bg-background px-2 font-mono text-[10px] text-muted-foreground outline-none" />
            {(filterQuery || filterCategory || filterSignal || filterRead || filterCompetitor || filterDomain || filterFrom || filterTo) && (
              <button
                onClick={() => {
                  setFilterQuery(""); setFilterCategory(""); setFilterSignal(""); setFilterRead("");
                  setFilterCompetitor(""); setFilterDomain(""); setFilterFrom(""); setFilterTo("");
                }}
                className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >Clear</button>
            )}
          </div>
        )}

        {/* Content area */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Insights list */}
          <div className={cn(
            "flex-1 overflow-y-auto min-w-0",
            selectedInsight && "hidden sm:block sm:max-w-sm lg:max-w-md border-r border-border"
          )}>
            {isLoadingInsights ? (
              <div className="p-4 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="rounded-lg border border-border overflow-hidden">
                    <div className="flex items-start gap-3 px-4 py-3 border-b border-border/50">
                      <Skeleton className="h-7 w-7 rounded shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-3/4" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                      <Skeleton className="h-4 w-16 rounded shrink-0" />
                    </div>
                    <div className="px-4 py-2.5 border-b border-border/50">
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-2/3 mt-1.5" />
                    </div>
                    <div className="px-4 py-2.5">
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : insights.length === 0 ? (
              <div className="flex h-full items-center justify-center p-8">
                <div className="text-center space-y-3 max-w-xs">
                  <div className="h-14 w-14 rounded-full border border-dashed border-border flex items-center justify-center mx-auto">
                    <Icon name="mail-01" size={22} className="text-muted-foreground/30" />
                  </div>
                  <div>
                    <p className="font-sans text-xs font-medium">No emails found</p>
                    <p className="font-sans text-xs text-muted-foreground mt-0.5">
                      {hasConnections
                        ? "Sync your inbox or adjust filters to see emails."
                        : "Connect a Gmail account to get started."}
                    </p>
                  </div>
                  {!hasConnections && (
                    <Button size="sm" variant="outline" onClick={handleConnect}>
                      <Icon name="mail-01" size={12} className="mr-1.5" />Connect Gmail
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-2.5">
                {insights.map(insight => (
                  <InsightCard
                    key={insight.id}
                    insight={insight}
                    onClick={() => handleInsightClick(insight)}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => loadInsights(page - 1)}
                      disabled={page <= 1}
                    >
                      <Icon name="arrow-left-01" size={12} className="mr-1.5" />Prev
                    </Button>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {page} / {totalPages}
                    </span>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => loadInsights(page + 1)}
                      disabled={page >= totalPages}
                    >
                      Next<Icon name="arrow-right-01" size={12} className="ml-1.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selectedInsight && (
            <div className={cn(
              "flex flex-col bg-card",
              "w-full sm:flex-1"
            )}>
              <InsightDetail insight={selectedInsight} onClose={() => setSelectedInsight(null)} />
            </div>
          )}
        </div>
      </div>

      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Gmail</DialogTitle>
            <DialogDescription>
              Peersheep will request read-only access to your Gmail inbox to identify competitor newsletters, SEO reports, testimonials, product updates, and pricing signals.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-foreground">What access includes</p>
            <ul className="space-y-1.5 font-sans text-xs text-muted-foreground">
              <li>Read email messages and metadata</li>
              <li>Analyze messages for competitive signals</li>
              <li>Continue syncing securely in the background</li>
            </ul>
            <p className="pt-1 font-sans text-xs text-muted-foreground">Peersheep cannot send, edit, or delete your emails.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentOpen(false)}>Cancel</Button>
            <Button onClick={() => void beginGmailConnect()}>
              <Icon name="mail-01" size={12} className="mr-1.5" />Continue to Google
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!disconnectTarget} onOpenChange={open => !open && setDisconnectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect Gmail?</DialogTitle>
            <DialogDescription>
              This will stop background syncing for {disconnectTarget?.email ?? "this account"}.
              Existing insights will remain available in Peersheep.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 p-4">
            <p className="font-sans text-xs text-amber-200">
              You can connect this account again later to resume syncing.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void confirmDisconnect()}>Disconnect Gmail</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
